import React from "react";
import { Mountain, Wind, Factory, TrendingUp } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

const CASE_META = [
  {
    icon: Mountain,
    key: "c1",
    result: "+25%",
    accent: "from-amber-500/10 to-orange-500/5",
    iconClass: "text-amber-600 bg-amber-50 ring-amber-100",
    testid: "case-mineria",
  },
  {
    icon: Wind,
    key: "c2",
    result: "-40%",
    accent: "from-sky-500/10 to-cyan-500/5",
    iconClass: "text-sky-600 bg-sky-50 ring-sky-100",
    testid: "case-energia",
  },
  {
    icon: Factory,
    key: "c3",
    result: "100%",
    accent: "from-purple-500/10 to-fuchsia-500/5",
    iconClass: "text-[#6B46C1] bg-purple-50 ring-purple-100",
    testid: "case-manufactura",
  },
];

export default function AtlasCases() {
  const { t } = useI18n();
  return (
    <section
      id="casos-exito"
      data-testid="atlas-cases"
      className="py-16 lg:py-24 bg-white"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.18em] text-[#6B46C1] uppercase bg-[#6B46C1]/10 rounded-full px-3 py-1.5">
            <TrendingUp size={14} />
            {t("cases.badge")}
          </div>
          <h2
            data-testid="cases-title"
            className="mt-4 font-display text-3xl sm:text-4xl lg:text-[44px] font-extrabold text-slate-900 leading-tight"
          >
            {t("cases.titleA")}{" "}
            <span className="text-[#6B46C1]">{t("cases.titleB")}</span>
          </h2>
          <p className="mt-4 text-slate-600 text-base sm:text-lg">
            {t("cases.subtitle")}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {CASE_META.map((c) => {
            const Icon = c.icon;
            return (
              <article
                key={c.testid}
                data-testid={c.testid}
                className="group relative rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-xl hover:shadow-slate-200/50 hover:-translate-y-1 transition-all duration-300"
              >
                <div
                  className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${c.accent} opacity-0 group-hover:opacity-100 transition-opacity`}
                  aria-hidden
                />
                <div className="relative p-7">
                  <div className="flex items-center justify-between">
                    <div
                      className={`h-12 w-12 rounded-xl flex items-center justify-center ring-1 ${c.iconClass}`}
                    >
                      <Icon size={24} strokeWidth={1.75} />
                    </div>
                    <span className="text-xs font-bold tracking-[0.15em] uppercase text-slate-400">
                      {t(`cases.${c.key}.industry`)}
                    </span>
                  </div>

                  <h3 className="mt-6 font-display text-xl font-extrabold text-slate-900 leading-tight">
                    {t(`cases.${c.key}.title`)}
                  </h3>

                  <div className="mt-5 flex items-baseline gap-2">
                    <span
                      data-testid={`${c.testid}-result`}
                      className="font-display text-4xl font-extrabold text-[#2563EB] leading-none tracking-tight"
                    >
                      {c.result}
                    </span>
                    <span className="text-sm text-slate-500">
                      {t(`cases.${c.key}.resultLabel`)}
                    </span>
                  </div>

                  <p className="mt-5 text-sm text-slate-600 leading-relaxed">
                    {t(`cases.${c.key}.desc`)}
                  </p>
                </div>

                <div
                  className="h-1 w-full bg-gradient-to-r from-transparent via-[#6B46C1]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-hidden
                />
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
